import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as ChartTooltip,
} from 'recharts';
import { queryDataSource } from '../../api/query';
import { sourceIdBul } from '../../utils/raporSonek';
import { SectionCard, fmtNum, fmtTRY } from '../yeni-design/primitives';
import { demoTheme } from '../../theme';
import type { ReportConfig } from '../../types/panelConfig';
import type { Donem } from './donem';

interface KanalSatiri {
  tarih: string;
  lokasyon: string;
  kanal_grup: string;
  kanal: string;
  fis_sayisi: number;
  net_ciro: number;
}

interface KanalOzet {
  kanalGrup: string;
  ciro: number;
  fis: number;
  sepet: number | null;
}

interface LokasyonOzet {
  lokasyon: string;
  magazaCiro: number;
  magazaFis: number;
  magazaSepet: number | null;
  digerKanallar: KanalOzet[];
}

interface Props {
  reports: ReportConfig[];
  donem: Donem;
}

/**
 * ONCEDEN (fix1'e kadar): 040 lokasyon dondurmuyordu, bu yuzden "es-magaza"
 * rakami 013'un ham cirosundan sirket capinda bir online toplami cikararak
 * TAHMIN EDILIYORDU - "hangi sube online tasiyor" da ayrica config'ten
 * okunan bir sabitti. O sinirlama artik YOK: 040 "Satış Kanalı Özeti" ARTIK
 * `lokasyon` kolonu tasiyor (canlida dogrulandi, kanal_grup toplamlari
 * degismedi). Dolayisiyla mağaza kanalı cirosu her lokasyon icin DOGRUDAN
 * OLCULEBILIYOR - cikarma, sirket capinda toplam, config'ten okunan "hangi
 * sube online tasiyor" sabiti TAMAMEN KALKTI. Olculmus ornek (Gizova, 90
 * gun): ATAKENT mağaza 17.040.147 (ayrica 134.493 kurumsal + 55 online +
 * -307 pazaryeri iade) · Merkez mağaza 4.556.161 (ayrica 10.589.885
 * pazaryeri + 3.087.883 kurumsal + 239.711 online). Eski "ATAKENT yalniz
 * magaza satiyor" varsayimi YANLIS cikti - ATAKENT'in de kurumsal/online
 * kanali var, sadece kucuk. Eski "Merkez magaza = 7.897.949" rakami da
 * yanlisti - o rakam kurumsalin magazaya katilmasindan kaynaklaniyordu.
 *
 * 013 (sube donemsel) artik KULLANILMIYOR: 040 ayni "magaza kanali cirosu"nu
 * dogrudan verirken, 013'un `degisim_pct`'i TUM kanallarin karisimidir (013
 * kanala gore ayrisamaz) - yani yanindaki rakamdan FARKLI bir tabana sahip
 * bir degisim yuzdesi gostermis olurduk. Boyle bir uyumsuzluk sessiz bir
 * yanlislik uretir; o yuzden donem-uzeri degisim burada hic gosterilmiyor.
 */
export function SubeBlok({ reports, donem }: Props) {
  const params = { '@BASTARIH': donem.bas, '@BITTARIH': donem.bit };
  // CONTROLLER RULING F4: sonek 40 + ad kontrolu (bkz. KanalBlok.tsx, ayni desen).
  const idKanal = sourceIdBul(reports, 40, 'Satış Kanalı Özeti');

  const kanalQ = useQuery({
    queryKey: ['sa-sube-kanal', idKanal, params],
    queryFn: () => queryDataSource<KanalSatiri>({ sourceId: idKanal!, parameters: params }),
    enabled: !!idKanal,
    throwOnError: false,
  });

  const lokasyonlar = useMemo<LokasyonOzet[]>(() => {
    const rows = kanalQ.data?.data ?? [];

    // lokasyon -> kanal_grup -> {ciro, fis} - `tarih` boyunca toplanir.
    // Sabit bir lokasyon/kanal_grup kumesi VARSAYILMAZ (`Bilinmeyen` lokasyon
    // ya da beklenmedik bir kanal_grup gelirse de dogru gruplanir).
    const map = new Map<string, Map<string, { ciro: number; fis: number }>>();
    for (const r of rows) {
      const lokasyon = String(r.lokasyon);
      const kanalGrup = String(r.kanal_grup);
      if (!map.has(lokasyon)) map.set(lokasyon, new Map());
      const grupMap = map.get(lokasyon)!;
      if (!grupMap.has(kanalGrup)) grupMap.set(kanalGrup, { ciro: 0, fis: 0 });
      const v = grupMap.get(kanalGrup)!;
      v.ciro += Number(r.net_ciro ?? 0);
      v.fis += Number(r.fis_sayisi ?? 0);
    }

    return Array.from(map.entries())
      .map(([lokasyon, grupMap]) => {
        const magaza = grupMap.get('Mağaza') ?? { ciro: 0, fis: 0 };
        const digerKanallar: KanalOzet[] = Array.from(grupMap.entries())
          .filter(([kanalGrup]) => kanalGrup !== 'Mağaza')
          .map(([kanalGrup, v]) => ({
            kanalGrup,
            ciro: v.ciro,
            fis: v.fis,
            // Sepet ortalamasi: fis=0 iken SIFIRA BOLME yapilmaz (ör. ATAKENT
            // Pazaryeri: 0 fis, -307 ciro - iade, pencere icinde satis yok).
            sepet: v.fis > 0 ? v.ciro / v.fis : null,
          }))
          .sort((a, b) => b.ciro - a.ciro);

        return {
          lokasyon,
          magazaCiro: magaza.ciro,
          magazaFis: magaza.fis,
          magazaSepet: magaza.fis > 0 ? magaza.ciro / magaza.fis : null,
          digerKanallar,
        };
      })
      .sort((a, b) => b.magazaCiro - a.magazaCiro);
  }, [kanalQ.data]);

  if (!idKanal) return null; // rapor yok/ad tutmuyor -> sorgu bile acilmaz
  if (lokasyonlar.length < 2) return null; // bos durum kurali: 2'den az lokasyon -> blok gizlenir

  return (
    <div data-testid="blok-sube">
      <SectionCard
        title="Şube karşılaştırması"
        subtitle="Mağaza kanalı cirosu (ölçülmüş); diğer kanallar ayrı gösterilir"
      >
        <div className="flex flex-col gap-4">
          <div style={{ width: '100%', height: 140 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={lokasyonlar.map((l) => ({ lokasyon: l.lokasyon, ciro: l.magazaCiro }))}
                layout="vertical"
                margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="2 4" stroke={demoTheme.border} horizontal={false} />
                <XAxis
                  type="number"
                  tickFormatter={(v) => fmtNum(Number(v))}
                  tick={{ fontSize: 11, fill: demoTheme.textMuted }}
                  axisLine={{ stroke: demoTheme.border }}
                  tickLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="lokasyon"
                  tick={{ fontSize: 12, fill: demoTheme.textPrimary }}
                  axisLine={false}
                  tickLine={false}
                  width={88}
                />
                <ChartTooltip formatter={(v) => fmtTRY(Number(v))} />
                <Bar dataKey="ciro" fill={demoTheme.primary} radius={[0, 2, 2, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="flex flex-col gap-3">
            {lokasyonlar.map((l) => (
              <div key={l.lokasyon} className="flex flex-col gap-1">
                <div
                  data-testid={`sube-${l.lokasyon}`}
                  className="flex items-center justify-between gap-3 py-1"
                >
                  <span className="text-[13px] font-medium text-ink-900 truncate">
                    {l.lokasyon} · Mağaza
                  </span>
                  <span className="text-[13px] text-ink-900 tnum">
                    {fmtTRY(l.magazaCiro)}
                    {l.magazaSepet !== null && (
                      <span className="text-ink-400 font-normal"> · sepet {fmtTRY(l.magazaSepet)}</span>
                    )}
                  </span>
                </div>
                {/* Diger kanallar: neden toplamlarin farkli oldugunu ACIKCA
                    gostermek icin ayri satirlarda - bare bir "magaza" rakami
                    baglamsiz sunulmaz. */}
                {l.digerKanallar.length > 0 && (
                  <div className="pl-4 flex flex-col gap-0.5">
                    {l.digerKanallar.map((k) => (
                      <div
                        key={k.kanalGrup}
                        data-testid={`sube-${l.lokasyon}-kanal-${k.kanalGrup}`}
                        className="flex items-center justify-between gap-3 text-[12px] text-ink-600"
                      >
                        <span className="truncate">{k.kanalGrup}</span>
                        <span className="tnum">
                          {fmtTRY(k.ciro)}
                          {k.sepet !== null && <span className="text-ink-400"> · sepet {fmtTRY(k.sepet)}</span>}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </SectionCard>
    </div>
  );
}
